import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Plus, Building, MapPin, Phone, Mail, Users, Edit, Trash2, UserPlus, Loader2, Search, RefreshCw, Globe, FileText, Calendar, Clock, DollarSign, Settings, Palmtree, Baby, Thermometer, Printer, FileSignature, BadgeCheck, XCircle, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PhoneInput } from '../components/ui/phone-input';

const ManageOffices = () => {
  const [offices, setOffices] = useState([]);
  const [users, setUsers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [officeEmployees, setOfficeEmployees] = useState([]);
  const [officeAdmins, setOfficeAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignAdminModal, setShowAssignAdminModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [adminSearchQuery, setAdminSearchQuery] = useState('');
  const [adminModalTab, setAdminModalTab] = useState('current');
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    location: '',
    address: '',
    timezone: 'Africa/Dar_es_Salaam',
    currency: 'TZS',
    registration_number: '',
    registration_date: '',
    tin: '',
    phone: '',
    email: '',
    website: ''
  });
  const [configData, setConfigData] = useState({
    employee_id_prefix: '',
    annual_leave_days: 21,
    sick_leave_days: 10,
    maternity_leave_days: 90,
    paternity_leave_days: 14,
    work_start_hour: '08:00',
    work_end_hour: '17:00',
    work_days_per_week: 5,
    working_days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    overtime_rate: 1.5,
    weekend_rate: 2.0,
    holiday_rate: 2.0,
    vat_percent: 18.0,
    public_holidays: [],
    sms_api_url: '',
    sms_api_auth: '',
    sms_sender_id: '',
  });
  const [newHoliday, setNewHoliday] = useState({ name: '', date: '', is_recurring: true });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [smsTestPhone, setSmsTestPhone] = useState('');
  const [smsTestCountryCode, setSmsTestCountryCode] = useState('+255');
  const [smsTestMessage, setSmsTestMessage] = useState('This is a test SMS from Ecowater Workspace.');
  const [smsTesting, setSmsTesting] = useState(false);

  const { getAuthHeaders } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchOffices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/offices/`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setOffices(data);
      }
    } catch (error) {
      console.error('Error fetching offices:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/?role=office_admin`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/system-config/countries`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCountries(data);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchOfficeEmployees = async (officeId) => {
    setLoadingEmployees(true);
    try {
      const headers = getAuthHeaders();
      headers['X-Office-ID'] = officeId;
      
      const response = await fetch(`${API_URL}/api/employees/`, {
        headers
      });
      if (response.ok) {
        const data = await response.json();
        setOfficeEmployees(data);
      }
    } catch (error) {
      console.error('Error fetching office employees:', error);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const fetchOfficeAdmins = async (officeId) => {
    try {
      const response = await fetch(`${API_URL}/api/users/office-admins/${officeId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setOfficeAdmins(data);
      }
    } catch (error) {
      console.error('Error fetching office admins:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setPageError(null);
      try {
        await Promise.all([fetchOffices(), fetchUsers(), fetchCountries()]);
      } catch (err) {
        console.error('Error loading page data:', err);
        setPageError('Failed to load page data. Please refresh.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleAddOffice = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/offices/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchOffices();
        setShowAddModal(false);
        resetForm();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create office');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditOffice = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const headers = getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      
      const response = await fetch(`${API_URL}/api/offices/${selectedOffice.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchOffices();
        setShowEditModal(false);
        resetForm();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to update office');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignAdmin = async (adminId) => {
    setError('');
    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/offices/${selectedOffice.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ office_admin_id: adminId || null })
      });

      if (response.ok) {
        await fetchOffices();
        setShowAssignAdminModal(false);
        setSelectedOffice(null);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to assign admin');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePromoteToAdmin = async (employeeId) => {
    if (!selectedOffice) return;
    setError('');
    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/users/promote-to-office-admin`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          employee_id: employeeId,
          office_id: selectedOffice.id
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Show success message
        if (data.new_account_created && data.default_pin) {
          alert(`Success! ${data.message}\n\nA new user account was created.\nDefault PIN: ${data.default_pin}\n\nThe employee should change this PIN on first login.`);
        } else {
          alert(data.message);
        }
        // Refresh data
        await Promise.all([
          fetchOffices(),
          fetchOfficeAdmins(selectedOffice.id),
          fetchUsers()
        ]);
        setAdminModalTab('current');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to promote employee to admin');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveAdminRole = async (userId) => {
    if (!selectedOffice) return;
    if (!window.confirm('Are you sure you want to remove the Office Admin role from this user?')) {
      return;
    }
    
    setError('');
    setSubmitting(true);

    try {
      const response = await fetch(
        `${API_URL}/api/users/remove-office-admin-role?user_id=${userId}&office_id=${selectedOffice.id}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders()
        }
      );

      if (response.ok) {
        await Promise.all([
          fetchOffices(),
          fetchOfficeAdmins(selectedOffice.id),
          fetchUsers()
        ]);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to remove admin role');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteOffice = async (officeId) => {
    if (!window.confirm('Are you sure you want to deactivate this office?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/offices/${officeId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        await fetchOffices();
      }
    } catch (error) {
      console.error('Error deleting office:', error);
    }
  };

  const handleReactivateOffice = async (officeId) => {
    try {
      const response = await fetch(`${API_URL}/api/offices/${officeId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: 'active' })
      });

      if (response.ok) {
        await fetchOffices();
      }
    } catch (error) {
      console.error('Error reactivating office:', error);
    }
  };

  const openEditModal = (office) => {
    setSelectedOffice(office);
    setFormData({
      name: office.name,
      country: office.country || '',
      location: office.location,
      address: office.address || '',
      timezone: office.timezone || 'Africa/Dar_es_Salaam',
      currency: office.currency || 'TZS',
      registration_number: office.registration_number || '',
      registration_date: office.registration_date || '',
      tin: office.tin || '',
      phone: office.phone || '',
      email: office.email || '',
      website: office.website || ''
    });
    setShowEditModal(true);
  };

  const openAssignAdminModal = async (office) => {
    setSelectedOffice(office);
    setAdminSearchQuery('');
    setAdminModalTab('current');
    setError('');
    setShowAssignAdminModal(true);
    // Fetch both employees and current admins for this office
    await Promise.all([
      fetchOfficeEmployees(office.id),
      fetchOfficeAdmins(office.id)
    ]);
  };

  const openConfigModal = (office) => {
    setSelectedOffice(office);
    const settings = office.settings || {};
    const leavePolicy = settings.leave_policy || {};
    const workingHours = settings.working_hours || {};
    const letterheadFooter = settings.letterhead_footer || {};
    
    setConfigData({
      employee_id_prefix: office.employee_id_prefix || '',
      annual_leave_days: leavePolicy.annual_leave_days || 21,
      sick_leave_days: leavePolicy.sick_leave_days || 10,
      maternity_leave_days: leavePolicy.maternity_leave_days || 90,
      paternity_leave_days: leavePolicy.paternity_leave_days || 14,
      work_start_hour: workingHours.start || '08:00',
      work_end_hour: workingHours.end || '17:00',
      work_days_per_week: settings.work_days_per_week || 5,
      working_days: Array.isArray(settings.working_days) && settings.working_days.length > 0
        ? settings.working_days
        : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      overtime_rate: settings.overtime_rate || 1.5,
      weekend_rate: settings.weekend_rate || 2.0,
      holiday_rate: settings.holiday_rate || 2.0,
      vat_percent: settings.vat_percent ?? 18.0,
      bank_name: settings.bank_name || '',
      bank_account_number: settings.bank_account_number || '',
      bank_account_name: settings.bank_account_name || '',
      public_holidays: settings.public_holidays || [],
      allow_employee_payslip_printing: settings.allow_employee_payslip_printing ?? true,
      letterhead_section1: letterheadFooter.section1 || '',
      letterhead_section2: letterheadFooter.section2 || '',
      letterhead_section3: letterheadFooter.section3 || '',
      sms_api_url: settings.sms_api_url || '',
      sms_api_auth: settings.sms_api_auth || '',
      sms_sender_id: settings.sms_sender_id || '',
    });
    setNewHoliday({ name: '', date: '', is_recurring: true });
    setShowConfigModal(true);
  };

  const handleSaveConfig = async () => {
    if (!selectedOffice) return;
    setSubmitting(true);
    setError('');

    try {
      const headers = getAuthHeaders();
      headers['Content-Type'] = 'application/json';

      const settingsUpdate = {
        employee_id_prefix: configData.employee_id_prefix || null,
        settings: {
          ...selectedOffice.settings,
          leave_policy: {
            annual_leave_days: configData.annual_leave_days,
            sick_leave_days: configData.sick_leave_days,
            maternity_leave_days: configData.maternity_leave_days,
            paternity_leave_days: configData.paternity_leave_days,
          },
          working_hours: {
            start: configData.work_start_hour,
            end: configData.work_end_hour,
          },
          work_days_per_week: (configData.working_days || []).length || configData.work_days_per_week,
          working_days: configData.working_days || [],
          overtime_rate: configData.overtime_rate,
          weekend_rate: configData.weekend_rate,
          holiday_rate: configData.holiday_rate,
          vat_percent: parseFloat(configData.vat_percent) || 0,
          bank_name: configData.bank_name || null,
          bank_account_number: configData.bank_account_number || null,
          bank_account_name: configData.bank_account_name || null,
          public_holidays: configData.public_holidays,
          allow_employee_payslip_printing: configData.allow_employee_payslip_printing,
          letterhead_footer: {
            section1: configData.letterhead_section1 || '',
            section2: configData.letterhead_section2 || '',
            section3: configData.letterhead_section3 || '',
          },
          sms_api_url: configData.sms_api_url || '',
          sms_api_auth: configData.sms_api_auth || '',
          sms_sender_id: configData.sms_sender_id || '',
        }
      };

      const response = await fetch(`${API_URL}/api/offices/${selectedOffice.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(settingsUpdate)
      });

      if (response.ok) {
        setShowConfigModal(false);
        await fetchOffices();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTestSms = async () => {
    if (!smsTestPhone) { toast.error('Enter a phone number to test'); return; }
    setSmsTesting(true);
    try {
      const headers = getAuthHeaders();
      headers['Content-Type'] = 'application/json';
      const res = await fetch(`${API_URL}/api/admin/send-test-sms`, {
        method: 'POST', headers,
        body: JSON.stringify({
          phone: `${smsTestCountryCode}${smsTestPhone}`,
          message: smsTestMessage,
          office_id: selectedOffice?.id,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Test SMS sent successfully!');
      } else {
        toast.error(data.detail || 'Failed to send test SMS');
      }
    } catch { toast.error('Network error'); }
    finally { setSmsTesting(false); }
  };

  const addHoliday = () => {
    if (!newHoliday.name || !newHoliday.date) return;
    setConfigData({
      ...configData,
      public_holidays: [...configData.public_holidays, { ...newHoliday }],
    });
    setNewHoliday({ name: '', date: '', is_recurring: true });
  };

  const removeHoliday = (index) => {
    setConfigData({
      ...configData,
      public_holidays: configData.public_holidays.filter((_, i) => i !== index),
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      country: '',
      location: '',
      address: '',
      timezone: 'Africa/Dar_es_Salaam',
      currency: 'TZS',
      registration_number: '',
      registration_date: '',
      tin: '',
      phone: '',
      email: '',
      website: ''
    });
    setSelectedOffice(null);
    setError('');
  };

  const filteredOffices = offices.filter(office =>
    office.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    office.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="offices-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="manage-offices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Manage Offices</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Create and manage company offices and their administrators
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
          className="flex items-center gap-2"
          data-testid="add-office-button"
        >
          <Plus className="h-4 w-4" />
          Add Office
        </Button>
      </div>

      {/* Error Display */}
      {pageError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-700">
              <span className="text-sm">{pageError}</span>
              <Button variant="ghost" size="sm" onClick={() => window.location.reload()}>
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Offices</p>
                <p className="text-2xl font-bold">{offices.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Employees</p>
                <p className="text-2xl font-bold">{offices.reduce((acc, o) => acc + (o.employee_count || 0), 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <UserPlus className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Office Admins Assigned</p>
                <p className="text-2xl font-bold">{offices.filter(o => o.office_admin_id).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search offices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-offices-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Office List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredOffices.map((office) => (
          <Card key={office.id} className="hover:shadow-md transition-shadow" data-testid={`office-card-${office.id}`}>
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Building className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">{office.name}</h3>
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <MapPin className="h-3 w-3" />
                        {office.location}
                      </div>
                    </div>
                  </div>
                  <Badge variant={office.status === 'active' ? 'default' : 'secondary'}>
                    {office.status}
                  </Badge>
                </div>

                {/* Details */}
                <div className="space-y-2 text-sm">
                  {office.country && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Globe className="h-4 w-4" />
                      {office.country}
                    </div>
                  )}
                  {office.registration_number && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <FileText className="h-4 w-4" />
                      Reg: {office.registration_number}
                    </div>
                  )}
                  {office.phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="h-4 w-4" />
                      {office.phone}
                    </div>
                  )}
                  {office.email && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="h-4 w-4" />
                      {office.email}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-600">
                    <Users className="h-4 w-4" />
                    {office.employee_count || 0} employees
                  </div>
                  {office.timezone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="h-4 w-4" />
                      {office.timezone.replace('_', ' ').replace('Africa/', '').replace('America/', '').replace('Europe/', '').replace('Asia/', '')}
                    </div>
                  )}
                  {office.currency && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <DollarSign className="h-4 w-4" />
                      {office.currency}
                    </div>
                  )}
                </div>

                {/* Admin Info */}
                <div className="pt-2 border-t">
                  {office.office_admin_name ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Office Admin</p>
                        <p className="text-sm font-medium">{office.office_admin_name}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openAssignAdminModal(office)}
                        data-testid={`change-admin-${office.id}`}
                      >
                        Change
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => openAssignAdminModal(office)}
                      data-testid={`assign-admin-${office.id}`}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Assign Admin
                    </Button>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => openEditModal(office)}
                    data-testid={`edit-office-${office.id}`}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => openConfigModal(office)}
                    data-testid={`config-office-${office.id}`}
                  >
                    <Settings className="h-4 w-4 mr-1" />
                    Configure
                  </Button>
                </div>
                <div className="flex gap-2 pt-2">
                  {office.status === 'active' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteOffice(office.id)}
                      data-testid={`delete-office-${office.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                      onClick={() => handleReactivateOffice(office.id)}
                      data-testid={`reactivate-office-${office.id}`}
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredOffices.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No offices found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {searchQuery ? 'Try adjusting your search' : 'Get started by adding your first office'}
            </p>
            {!searchQuery && (
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Office
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Office Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-office-modal">
          <DialogHeader>
            <DialogTitle>Add New Office</DialogTitle>
            <DialogDescription>Create a new office/company for your organization</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddOffice} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Company Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Ecowater Tanzania Ltd"
                required
                data-testid="office-name-input"
              />
            </div>
            
            {/* Country and Location */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="country">Country *</Label>
                <Select 
                  value={formData.country} 
                  onValueChange={(value) => setFormData({ ...formData, country: value })}
                >
                  <SelectTrigger data-testid="office-country-select">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.id} value={country.name}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">City/Location *</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g., Dar es Salaam"
                  required
                  data-testid="office-location-input"
                />
              </div>
            </div>
            
            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Full physical address"
                data-testid="office-address-input"
              />
            </div>

            {/* Timezone */}
            <div className="space-y-2">
              <Label htmlFor="timezone">Timezone *</Label>
              <Select 
                value={formData.timezone} 
                onValueChange={(value) => setFormData({ ...formData, timezone: value })}
              >
                <SelectTrigger data-testid="office-timezone-select">
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {/* Africa */}
                  <SelectItem value="Africa/Nairobi">East Africa Time (Kenya, Uganda)</SelectItem>
                  <SelectItem value="Africa/Dar_es_Salaam">East Africa Time (Tanzania)</SelectItem>
                  <SelectItem value="Africa/Kigali">Central Africa Time (Rwanda)</SelectItem>
                  <SelectItem value="Africa/Johannesburg">South Africa Time</SelectItem>
                  <SelectItem value="Africa/Lagos">West Africa Time (Nigeria)</SelectItem>
                  <SelectItem value="Africa/Cairo">Egypt Time</SelectItem>
                  {/* Americas */}
                  <SelectItem value="America/New_York">Eastern Time (US)</SelectItem>
                  <SelectItem value="America/Chicago">Central Time (US)</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific Time (US)</SelectItem>
                  {/* Europe */}
                  <SelectItem value="Europe/London">GMT (London)</SelectItem>
                  <SelectItem value="Europe/Paris">Central European Time</SelectItem>
                  {/* Asia */}
                  <SelectItem value="Asia/Dubai">Gulf Standard Time</SelectItem>
                  <SelectItem value="Asia/Singapore">Singapore Time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Currency */}
            <div className="space-y-2">
              <Label htmlFor="currency">Default Currency *</Label>
              <Select 
                value={formData.currency} 
                onValueChange={(value) => setFormData({ ...formData, currency: value })}
              >
                <SelectTrigger data-testid="office-currency-select">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {/* Africa */}
                  <SelectItem value="KES">KES - Kenyan Shilling</SelectItem>
                  <SelectItem value="TZS">TZS - Tanzanian Shilling</SelectItem>
                  <SelectItem value="UGX">UGX - Ugandan Shilling</SelectItem>
                  <SelectItem value="RWF">RWF - Rwandan Franc</SelectItem>
                  <SelectItem value="ETB">ETB - Ethiopian Birr</SelectItem>
                  <SelectItem value="ZAR">ZAR - South African Rand</SelectItem>
                  <SelectItem value="NGN">NGN - Nigerian Naira</SelectItem>
                  <SelectItem value="GHS">GHS - Ghanaian Cedi</SelectItem>
                  <SelectItem value="EGP">EGP - Egyptian Pound</SelectItem>
                  <SelectItem value="MAD">MAD - Moroccan Dirham</SelectItem>
                  <SelectItem value="XOF">XOF - West African CFA Franc</SelectItem>
                  <SelectItem value="XAF">XAF - Central African CFA Franc</SelectItem>
                  <SelectItem value="BWP">BWP - Botswana Pula</SelectItem>
                  <SelectItem value="MUR">MUR - Mauritian Rupee</SelectItem>
                  <SelectItem value="ZMW">ZMW - Zambian Kwacha</SelectItem>
                  <SelectItem value="MWK">MWK - Malawian Kwacha</SelectItem>
                  <SelectItem value="AOA">AOA - Angolan Kwanza</SelectItem>
                  <SelectItem value="DZD">DZD - Algerian Dinar</SelectItem>
                  <SelectItem value="TND">TND - Tunisian Dinar</SelectItem>
                  <SelectItem value="LYD">LYD - Libyan Dinar</SelectItem>
                  <SelectItem value="SDG">SDG - Sudanese Pound</SelectItem>
                  {/* Americas */}
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                  <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                  <SelectItem value="MXN">MXN - Mexican Peso</SelectItem>
                  <SelectItem value="BRL">BRL - Brazilian Real</SelectItem>
                  <SelectItem value="ARS">ARS - Argentine Peso</SelectItem>
                  <SelectItem value="CLP">CLP - Chilean Peso</SelectItem>
                  <SelectItem value="COP">COP - Colombian Peso</SelectItem>
                  <SelectItem value="PEN">PEN - Peruvian Sol</SelectItem>
                  {/* Europe */}
                  <SelectItem value="EUR">EUR - Euro</SelectItem>
                  <SelectItem value="GBP">GBP - British Pound</SelectItem>
                  <SelectItem value="CHF">CHF - Swiss Franc</SelectItem>
                  <SelectItem value="SEK">SEK - Swedish Krona</SelectItem>
                  <SelectItem value="NOK">NOK - Norwegian Krone</SelectItem>
                  <SelectItem value="DKK">DKK - Danish Krone</SelectItem>
                  <SelectItem value="PLN">PLN - Polish Zloty</SelectItem>
                  <SelectItem value="CZK">CZK - Czech Koruna</SelectItem>
                  <SelectItem value="HUF">HUF - Hungarian Forint</SelectItem>
                  <SelectItem value="RON">RON - Romanian Leu</SelectItem>
                  <SelectItem value="RUB">RUB - Russian Ruble</SelectItem>
                  <SelectItem value="UAH">UAH - Ukrainian Hryvnia</SelectItem>
                  <SelectItem value="TRY">TRY - Turkish Lira</SelectItem>
                  {/* Asia */}
                  <SelectItem value="JPY">JPY - Japanese Yen</SelectItem>
                  <SelectItem value="CNY">CNY - Chinese Yuan</SelectItem>
                  <SelectItem value="HKD">HKD - Hong Kong Dollar</SelectItem>
                  <SelectItem value="SGD">SGD - Singapore Dollar</SelectItem>
                  <SelectItem value="KRW">KRW - South Korean Won</SelectItem>
                  <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                  <SelectItem value="PKR">PKR - Pakistani Rupee</SelectItem>
                  <SelectItem value="BDT">BDT - Bangladeshi Taka</SelectItem>
                  <SelectItem value="LKR">LKR - Sri Lankan Rupee</SelectItem>
                  <SelectItem value="NPR">NPR - Nepalese Rupee</SelectItem>
                  <SelectItem value="THB">THB - Thai Baht</SelectItem>
                  <SelectItem value="VND">VND - Vietnamese Dong</SelectItem>
                  <SelectItem value="MYR">MYR - Malaysian Ringgit</SelectItem>
                  <SelectItem value="IDR">IDR - Indonesian Rupiah</SelectItem>
                  <SelectItem value="PHP">PHP - Philippine Peso</SelectItem>
                  <SelectItem value="TWD">TWD - Taiwan Dollar</SelectItem>
                  {/* Middle East */}
                  <SelectItem value="AED">AED - UAE Dirham</SelectItem>
                  <SelectItem value="SAR">SAR - Saudi Riyal</SelectItem>
                  <SelectItem value="QAR">QAR - Qatari Riyal</SelectItem>
                  <SelectItem value="KWD">KWD - Kuwaiti Dinar</SelectItem>
                  <SelectItem value="BHD">BHD - Bahraini Dinar</SelectItem>
                  <SelectItem value="OMR">OMR - Omani Rial</SelectItem>
                  <SelectItem value="JOD">JOD - Jordanian Dinar</SelectItem>
                  <SelectItem value="ILS">ILS - Israeli Shekel</SelectItem>
                  <SelectItem value="IQD">IQD - Iraqi Dinar</SelectItem>
                  <SelectItem value="IRR">IRR - Iranian Rial</SelectItem>
                  {/* Oceania */}
                  <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                  <SelectItem value="NZD">NZD - New Zealand Dollar</SelectItem>
                  <SelectItem value="FJD">FJD - Fiji Dollar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Registration Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="registration_number">Registration Number</Label>
                <Input
                  id="registration_number"
                  value={formData.registration_number}
                  onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                  placeholder="Company registration no."
                  data-testid="office-registration-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="registration_date">Registration Date</Label>
                <Input
                  id="registration_date"
                  type="date"
                  value={formData.registration_date}
                  onChange={(e) => setFormData({ ...formData, registration_date: e.target.value })}
                  data-testid="office-registration-date-input"
                />
              </div>
            </div>
            
            {/* TIN */}
            <div className="space-y-2">
              <Label htmlFor="tin">TIN (Tax Identification Number)</Label>
              <Input
                id="tin"
                value={formData.tin}
                onChange={(e) => setFormData({ ...formData, tin: e.target.value })}
                placeholder="Tax ID number"
                data-testid="office-tin-input"
              />
            </div>
            
            {/* Contact Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <PhoneInput
                  id="phone"
                  value={formData.phone}
                  onChange={(value) => setFormData({ ...formData, phone: value || '' })}
                  placeholder="Office phone number"
                  data-testid="office-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Office email"
                  data-testid="office-email-input"
                />
              </div>
            </div>
            
            {/* Website */}
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://www.example.com"
                data-testid="office-website-input"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-office-button">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Office'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Office Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-office-modal">
          <DialogHeader>
            <DialogTitle>Edit Office</DialogTitle>
            <DialogDescription>Update office information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditOffice} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="edit-name">Company Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                data-testid="edit-office-name-input"
              />
            </div>
            
            {/* Country and Location */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-country">Country</Label>
                <Select 
                  value={formData.country} 
                  onValueChange={(value) => setFormData({ ...formData, country: value })}
                >
                  <SelectTrigger data-testid="edit-office-country-select">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.id} value={country.name}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-location">City/Location *</Label>
                <Input
                  id="edit-location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                  data-testid="edit-office-location-input"
                />
              </div>
            </div>
            
            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="edit-address">Address</Label>
              <Input
                id="edit-address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                data-testid="edit-office-address-input"
              />
            </div>

            {/* Timezone */}
            <div className="space-y-2">
              <Label htmlFor="edit-timezone">Timezone</Label>
              <Select 
                value={formData.timezone} 
                onValueChange={(value) => setFormData({ ...formData, timezone: value })}
              >
                <SelectTrigger data-testid="edit-office-timezone-select">
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {/* Africa */}
                  <SelectItem value="Africa/Nairobi">East Africa Time (Kenya, Uganda)</SelectItem>
                  <SelectItem value="Africa/Dar_es_Salaam">East Africa Time (Tanzania)</SelectItem>
                  <SelectItem value="Africa/Kigali">Central Africa Time (Rwanda)</SelectItem>
                  <SelectItem value="Africa/Johannesburg">South Africa Time</SelectItem>
                  <SelectItem value="Africa/Lagos">West Africa Time (Nigeria)</SelectItem>
                  <SelectItem value="Africa/Cairo">Egypt Time</SelectItem>
                  {/* Americas */}
                  <SelectItem value="America/New_York">Eastern Time (US)</SelectItem>
                  <SelectItem value="America/Chicago">Central Time (US)</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific Time (US)</SelectItem>
                  {/* Europe */}
                  <SelectItem value="Europe/London">GMT (London)</SelectItem>
                  <SelectItem value="Europe/Paris">Central European Time</SelectItem>
                  {/* Asia */}
                  <SelectItem value="Asia/Dubai">Gulf Standard Time</SelectItem>
                  <SelectItem value="Asia/Singapore">Singapore Time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Currency */}
            <div className="space-y-2">
              <Label htmlFor="edit-currency">Default Currency</Label>
              <Select 
                value={formData.currency} 
                onValueChange={(value) => setFormData({ ...formData, currency: value })}
              >
                <SelectTrigger data-testid="edit-office-currency-select">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {/* Africa */}
                  <SelectItem value="KES">KES - Kenyan Shilling</SelectItem>
                  <SelectItem value="TZS">TZS - Tanzanian Shilling</SelectItem>
                  <SelectItem value="UGX">UGX - Ugandan Shilling</SelectItem>
                  <SelectItem value="RWF">RWF - Rwandan Franc</SelectItem>
                  <SelectItem value="ETB">ETB - Ethiopian Birr</SelectItem>
                  <SelectItem value="ZAR">ZAR - South African Rand</SelectItem>
                  <SelectItem value="NGN">NGN - Nigerian Naira</SelectItem>
                  <SelectItem value="GHS">GHS - Ghanaian Cedi</SelectItem>
                  <SelectItem value="EGP">EGP - Egyptian Pound</SelectItem>
                  <SelectItem value="MAD">MAD - Moroccan Dirham</SelectItem>
                  <SelectItem value="XOF">XOF - West African CFA Franc</SelectItem>
                  <SelectItem value="XAF">XAF - Central African CFA Franc</SelectItem>
                  <SelectItem value="BWP">BWP - Botswana Pula</SelectItem>
                  <SelectItem value="MUR">MUR - Mauritian Rupee</SelectItem>
                  <SelectItem value="ZMW">ZMW - Zambian Kwacha</SelectItem>
                  <SelectItem value="MWK">MWK - Malawian Kwacha</SelectItem>
                  <SelectItem value="AOA">AOA - Angolan Kwanza</SelectItem>
                  <SelectItem value="DZD">DZD - Algerian Dinar</SelectItem>
                  <SelectItem value="TND">TND - Tunisian Dinar</SelectItem>
                  <SelectItem value="LYD">LYD - Libyan Dinar</SelectItem>
                  <SelectItem value="SDG">SDG - Sudanese Pound</SelectItem>
                  {/* Americas */}
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                  <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                  <SelectItem value="MXN">MXN - Mexican Peso</SelectItem>
                  <SelectItem value="BRL">BRL - Brazilian Real</SelectItem>
                  <SelectItem value="ARS">ARS - Argentine Peso</SelectItem>
                  <SelectItem value="CLP">CLP - Chilean Peso</SelectItem>
                  <SelectItem value="COP">COP - Colombian Peso</SelectItem>
                  <SelectItem value="PEN">PEN - Peruvian Sol</SelectItem>
                  {/* Europe */}
                  <SelectItem value="EUR">EUR - Euro</SelectItem>
                  <SelectItem value="GBP">GBP - British Pound</SelectItem>
                  <SelectItem value="CHF">CHF - Swiss Franc</SelectItem>
                  <SelectItem value="SEK">SEK - Swedish Krona</SelectItem>
                  <SelectItem value="NOK">NOK - Norwegian Krone</SelectItem>
                  <SelectItem value="DKK">DKK - Danish Krone</SelectItem>
                  <SelectItem value="PLN">PLN - Polish Zloty</SelectItem>
                  <SelectItem value="CZK">CZK - Czech Koruna</SelectItem>
                  <SelectItem value="HUF">HUF - Hungarian Forint</SelectItem>
                  <SelectItem value="RON">RON - Romanian Leu</SelectItem>
                  <SelectItem value="RUB">RUB - Russian Ruble</SelectItem>
                  <SelectItem value="UAH">UAH - Ukrainian Hryvnia</SelectItem>
                  <SelectItem value="TRY">TRY - Turkish Lira</SelectItem>
                  {/* Asia */}
                  <SelectItem value="JPY">JPY - Japanese Yen</SelectItem>
                  <SelectItem value="CNY">CNY - Chinese Yuan</SelectItem>
                  <SelectItem value="HKD">HKD - Hong Kong Dollar</SelectItem>
                  <SelectItem value="SGD">SGD - Singapore Dollar</SelectItem>
                  <SelectItem value="KRW">KRW - South Korean Won</SelectItem>
                  <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                  <SelectItem value="PKR">PKR - Pakistani Rupee</SelectItem>
                  <SelectItem value="BDT">BDT - Bangladeshi Taka</SelectItem>
                  <SelectItem value="LKR">LKR - Sri Lankan Rupee</SelectItem>
                  <SelectItem value="NPR">NPR - Nepalese Rupee</SelectItem>
                  <SelectItem value="THB">THB - Thai Baht</SelectItem>
                  <SelectItem value="VND">VND - Vietnamese Dong</SelectItem>
                  <SelectItem value="MYR">MYR - Malaysian Ringgit</SelectItem>
                  <SelectItem value="IDR">IDR - Indonesian Rupiah</SelectItem>
                  <SelectItem value="PHP">PHP - Philippine Peso</SelectItem>
                  <SelectItem value="TWD">TWD - Taiwan Dollar</SelectItem>
                  {/* Middle East */}
                  <SelectItem value="AED">AED - UAE Dirham</SelectItem>
                  <SelectItem value="SAR">SAR - Saudi Riyal</SelectItem>
                  <SelectItem value="QAR">QAR - Qatari Riyal</SelectItem>
                  <SelectItem value="KWD">KWD - Kuwaiti Dinar</SelectItem>
                  <SelectItem value="BHD">BHD - Bahraini Dinar</SelectItem>
                  <SelectItem value="OMR">OMR - Omani Rial</SelectItem>
                  <SelectItem value="JOD">JOD - Jordanian Dinar</SelectItem>
                  <SelectItem value="ILS">ILS - Israeli Shekel</SelectItem>
                  <SelectItem value="IQD">IQD - Iraqi Dinar</SelectItem>
                  <SelectItem value="IRR">IRR - Iranian Rial</SelectItem>
                  {/* Oceania */}
                  <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                  <SelectItem value="NZD">NZD - New Zealand Dollar</SelectItem>
                  <SelectItem value="FJD">FJD - Fiji Dollar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Registration Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-registration_number">Registration Number</Label>
                <Input
                  id="edit-registration_number"
                  value={formData.registration_number}
                  onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                  data-testid="edit-office-registration-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-registration_date">Registration Date</Label>
                <Input
                  id="edit-registration_date"
                  type="date"
                  value={formData.registration_date}
                  onChange={(e) => setFormData({ ...formData, registration_date: e.target.value })}
                  data-testid="edit-office-registration-date-input"
                />
              </div>
            </div>
            
            {/* TIN */}
            <div className="space-y-2">
              <Label htmlFor="edit-tin">TIN (Tax Identification Number)</Label>
              <Input
                id="edit-tin"
                value={formData.tin}
                onChange={(e) => setFormData({ ...formData, tin: e.target.value })}
                data-testid="edit-office-tin-input"
              />
            </div>
            
            {/* Contact Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Phone</Label>
                <PhoneInput
                  id="edit-phone"
                  value={formData.phone}
                  onChange={(value) => setFormData({ ...formData, phone: value || '' })}
                  data-testid="edit-office-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="edit-office-email-input"
                />
              </div>
            </div>
            
            {/* Website */}
            <div className="space-y-2">
              <Label htmlFor="edit-website">Website</Label>
              <Input
                id="edit-website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                data-testid="edit-office-website-input"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="update-office-button">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Office'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Admin Modal - Enhanced with Employee Mapping */}
      <Dialog open={showAssignAdminModal} onOpenChange={setShowAssignAdminModal}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="assign-admin-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Manage Office Admins
            </DialogTitle>
            <DialogDescription>
              {selectedOffice?.name} - Assign admins or promote employees
            </DialogDescription>
          </DialogHeader>
          
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Tabs value={adminModalTab} onValueChange={setAdminModalTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="current" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Current Admins ({officeAdmins.length})
              </TabsTrigger>
              <TabsTrigger value="promote" className="flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                Promote Employee
              </TabsTrigger>
            </TabsList>

            {/* Current Admins Tab */}
            <TabsContent value="current" className="flex-1 overflow-y-auto mt-4">
              {officeAdmins.length > 0 ? (
                <div className="space-y-2">
                  {officeAdmins.map(admin => (
                    <Card key={admin.user_id} className="border-green-200 bg-green-50">
                      <CardContent className="p-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <Users className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium">{admin.full_name}</p>
                            <p className="text-xs text-gray-500">{admin.phone}</p>
                            {admin.job_title && (
                              <p className="text-xs text-green-600">{admin.job_title}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-green-100 text-green-700">Admin</Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleRemoveAdminRole(admin.user_id)}
                            disabled={submitting}
                            data-testid={`remove-admin-role-${admin.user_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No office admins assigned</p>
                  <p className="text-sm text-gray-500">Use the "Promote Employee" tab to add an admin</p>
                </div>
              )}
            </TabsContent>

            {/* Promote Employee Tab */}
            <TabsContent value="promote" className="flex-1 overflow-hidden flex flex-col mt-4">
              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search employees by name or phone..."
                  value={adminSearchQuery}
                  onChange={(e) => setAdminSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-employees-for-admin"
                />
              </div>

              {loadingEmployees ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto space-y-2">
                  {officeEmployees
                    .filter(emp => {
                      const search = adminSearchQuery.toLowerCase();
                      const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
                      const phone = emp.contact?.phone || '';
                      return !search || fullName.includes(search) || phone.includes(search);
                    })
                    .filter(emp => {
                      // Exclude employees who are already admins for this office
                      return !officeAdmins.some(admin => admin.employee_id === emp.id);
                    })
                    .map(emp => (
                      <Card 
                        key={emp.id} 
                        className="hover:border-blue-400 transition-colors cursor-pointer"
                        data-testid={`promote-employee-${emp.id}`}
                      >
                        <CardContent className="p-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden">
                              {emp.profile_picture ? (
                                <img src={emp.profile_picture} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Users className="h-5 w-5 text-gray-500" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium">{emp.first_name} {emp.last_name}</p>
                              <p className="text-xs text-gray-500">
                                {emp.employment?.job_title || 'No title'} • {emp.employment?.employee_id || 'No ID'}
                              </p>
                              {emp.contact?.phone && (
                                <p className="text-xs text-gray-400">{emp.contact.phone}</p>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handlePromoteToAdmin(emp.id)}
                            disabled={submitting}
                            className="flex items-center gap-1"
                          >
                            {submitting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <UserPlus className="h-4 w-4" />
                                Promote
                              </>
                            )}
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  
                  {officeEmployees.filter(emp => {
                    const search = adminSearchQuery.toLowerCase();
                    const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
                    const phone = emp.contact?.phone || '';
                    return !search || fullName.includes(search) || phone.includes(search);
                  }).filter(emp => !officeAdmins.some(admin => admin.employee_id === emp.id)).length === 0 && (
                    <div className="text-center py-8">
                      <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">
                        {adminSearchQuery ? 'No matching employees found' : 'All employees are already admins'}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowAssignAdminModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Office Configuration Modal */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="config-office-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configure {selectedOffice?.name}
            </DialogTitle>
            <DialogDescription>
              Set up leave policies, working hours, and public holidays for this office
            </DialogDescription>
          </DialogHeader>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full grid-cols-7">
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="leave">Leave Policy</TabsTrigger>
              <TabsTrigger value="hours">Working Hours</TabsTrigger>
              <TabsTrigger value="holidays">Holidays</TabsTrigger>
              <TabsTrigger value="payroll">Payroll</TabsTrigger>
              <TabsTrigger value="letterhead">Letterhead</TabsTrigger>
              <TabsTrigger value="sms">SMS</TabsTrigger>
            </TabsList>

            {/* General Tab */}
            <TabsContent value="general" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="employee_id_prefix" className="text-sm font-medium">
                    Employee ID Prefix
                  </Label>
                  <Input
                    id="employee_id_prefix"
                    value={configData.employee_id_prefix || ''}
                    onChange={(e) => setConfigData({ ...configData, employee_id_prefix: e.target.value.toUpperCase() })}
                    placeholder="e.g., ECO, TZ, KE"
                    maxLength={5}
                    className="uppercase"
                    data-testid="employee-id-prefix-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    This prefix will be used when generating employee IDs (e.g., ECO-123456789). 
                    Leave blank to use the first 3 letters of the office name.
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* Leave Policy Tab */}
            <TabsContent value="leave" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs flex items-center gap-1">
                    <Palmtree className="h-3 w-3" /> Annual Leave
                  </Label>
                  <Input
                    type="number"
                    value={configData.annual_leave_days}
                    onChange={(e) => setConfigData({ ...configData, annual_leave_days: parseInt(e.target.value) || 0 })}
                    placeholder="Days"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs flex items-center gap-1">
                    <Thermometer className="h-3 w-3" /> Sick Leave
                  </Label>
                  <Input
                    type="number"
                    value={configData.sick_leave_days}
                    onChange={(e) => setConfigData({ ...configData, sick_leave_days: parseInt(e.target.value) || 0 })}
                    placeholder="Days"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs flex items-center gap-1">
                    <Baby className="h-3 w-3" /> Maternity Leave
                  </Label>
                  <Input
                    type="number"
                    value={configData.maternity_leave_days}
                    onChange={(e) => setConfigData({ ...configData, maternity_leave_days: parseInt(e.target.value) || 0 })}
                    placeholder="Days"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Paternity Leave</Label>
                  <Input
                    type="number"
                    value={configData.paternity_leave_days}
                    onChange={(e) => setConfigData({ ...configData, paternity_leave_days: parseInt(e.target.value) || 0 })}
                    placeholder="Days"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Working Hours Tab */}
            <TabsContent value="hours" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs">Work Start Time</Label>
                  <Input
                    type="time"
                    value={configData.work_start_hour}
                    onChange={(e) => setConfigData({ ...configData, work_start_hour: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Work End Time</Label>
                  <Input
                    type="time"
                    value={configData.work_end_hour}
                    onChange={(e) => setConfigData({ ...configData, work_end_hour: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Working Days</Label>
                <p className="text-[11px] text-muted-foreground">
                  Employees will only be required to check in on the days selected below.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 pt-1">
                  {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => {
                    const selected = (configData.working_days || []).includes(day);
                    return (
                      <label
                        key={day}
                        data-testid={`working-day-${day.toLowerCase()}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors text-sm ${
                          selected
                            ? 'bg-blue-50 border-blue-500 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                            : 'bg-background border-border hover:bg-muted'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-blue-600"
                          checked={selected}
                          onChange={(e) => {
                            const current = configData.working_days || [];
                            const next = e.target.checked
                              ? [...current, day]
                              : current.filter((d) => d !== day);
                            // Preserve canonical week order
                            const order = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                            next.sort((a, b) => order.indexOf(a) - order.indexOf(b));
                            setConfigData({
                              ...configData,
                              working_days: next,
                              work_days_per_week: next.length || configData.work_days_per_week,
                            });
                          }}
                        />
                        <span>{day.slice(0, 3)}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground pt-1">
                  Selected: <span className="font-medium">{(configData.working_days || []).length}</span> day(s) per week
                </p>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" /> Overtime Rates (Multiplier)
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Overtime Rate</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={configData.overtime_rate}
                      onChange={(e) => setConfigData({ ...configData, overtime_rate: parseFloat(e.target.value) || 1.5 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Weekend Rate</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={configData.weekend_rate}
                      onChange={(e) => setConfigData({ ...configData, weekend_rate: parseFloat(e.target.value) || 2.0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Holiday Rate</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={configData.holiday_rate}
                      onChange={(e) => setConfigData({ ...configData, holiday_rate: parseFloat(e.target.value) || 2.0 })}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Public Holidays Tab */}
            <TabsContent value="holidays" className="space-y-4 mt-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Holiday name (e.g., Uhuru Day)"
                  value={newHoliday.name}
                  onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                  className="flex-1"
                />
                <Input
                  type="date"
                  value={newHoliday.date}
                  onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
                  className="w-40"
                />
                <Button type="button" onClick={addHoliday} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {configData.public_holidays.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                  {configData.public_holidays.map((h, i) => (
                    <div key={i} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-2 rounded">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium">{h.name}</span>
                        <span className="text-xs text-gray-500">{h.date}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeHoliday(i)}>
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 border rounded-lg">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">No public holidays configured</p>
                  <p className="text-sm text-gray-500">Add holidays using the form above</p>
                </div>
              )}
            </TabsContent>

            {/* Payroll Settings Tab */}
            <TabsContent value="payroll" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="p-4 rounded-lg border bg-blue-50/50 dark:bg-blue-950/20">
                  <Label className="text-sm font-semibold mb-2 block">VAT / Tax Rate (%)</Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Default tax percentage applied to outgoing invoices when "Apply VAT" is checked.
                  </p>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={configData.vat_percent}
                    onChange={(e) => setConfigData({ ...configData, vat_percent: e.target.value })}
                    placeholder="e.g. 18"
                    data-testid="vat-percent-input"
                    className="max-w-[200px]"
                  />
                </div>

                <div className="p-4 rounded-lg border bg-emerald-50/40 dark:bg-emerald-950/20">
                  <Label className="text-sm font-semibold mb-2 block">Invoice Payment Details</Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Optional. Bank account details shown on the Payment Method section of outgoing invoice PDFs.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Bank Name</Label>
                      <Input
                        value={configData.bank_name}
                        onChange={(e) => setConfigData({ ...configData, bank_name: e.target.value })}
                        placeholder="e.g. CRDB Bank"
                        data-testid="bank-name-input"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Account Number</Label>
                      <Input
                        value={configData.bank_account_number}
                        onChange={(e) => setConfigData({ ...configData, bank_account_number: e.target.value })}
                        placeholder="e.g. 0150-123456789"
                        data-testid="bank-account-number-input"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Account Name</Label>
                      <Input
                        value={configData.bank_account_name}
                        onChange={(e) => setConfigData({ ...configData, bank_account_name: e.target.value })}
                        placeholder="e.g. Ecowater Tanzania Ltd"
                        data-testid="bank-account-name-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg border bg-gray-50 dark:bg-gray-800">
                  <div className="space-y-1">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Printer className="h-4 w-4" />
                      Allow Employees to Print Payslips
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      When enabled, employees can print their payslips from "My Salary" page.
                    </p>
                  </div>
                  <Switch
                    checked={configData.allow_employee_payslip_printing}
                    onCheckedChange={(checked) => setConfigData({ ...configData, allow_employee_payslip_printing: checked })}
                    data-testid="allow-payslip-printing-switch"
                  />
                </div>
                <Alert className={configData.allow_employee_payslip_printing ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}>
                  <AlertDescription className={configData.allow_employee_payslip_printing ? 'text-green-700' : 'text-amber-700'}>
                    {configData.allow_employee_payslip_printing
                      ? 'Employees can view and print their payslips.'
                      : 'Employees can view but cannot print their payslips. Only admins can print.'}
                  </AlertDescription>
                </Alert>
              </div>
            </TabsContent>

            {/* Letterhead Footer Settings Tab */}
            <TabsContent value="letterhead" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <FileSignature className="h-5 w-5 text-blue-600" />
                  <h3 className="font-medium">Letterhead Footer Configuration</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Configure the footer that appears on generated letters. Each section appears in a column (left, middle, right).
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Section 1 (Left)</Label>
                    <Textarea
                      value={configData.letterhead_section1 || ''}
                      onChange={(e) => setConfigData({ ...configData, letterhead_section1: e.target.value })}
                      placeholder="Company Name&#10;Email&#10;Phone"
                      rows={4}
                      className="resize-none"
                      data-testid="letterhead-section1"
                    />
                    <p className="text-xs text-muted-foreground">
                      e.g., Company name, email, phone (one per line)
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Section 2 (Middle)</Label>
                    <Textarea
                      value={configData.letterhead_section2 || ''}
                      onChange={(e) => setConfigData({ ...configData, letterhead_section2: e.target.value })}
                      placeholder="Plot No 38, Block E&#10;P.O. Box 693&#10;City, Country"
                      rows={4}
                      className="resize-none"
                      data-testid="letterhead-section2"
                    />
                    <p className="text-xs text-muted-foreground">
                      e.g., Physical address lines
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Section 3 (Right)</Label>
                    <Textarea
                      value={configData.letterhead_section3 || ''}
                      onChange={(e) => setConfigData({ ...configData, letterhead_section3: e.target.value })}
                      placeholder="www.yourcompany.com"
                      rows={4}
                      className="resize-none"
                      data-testid="letterhead-section3"
                    />
                    <p className="text-xs text-muted-foreground">
                      e.g., Website, registration numbers
                    </p>
                  </div>
                </div>
                
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertDescription className="text-blue-700">
                    If left empty, the footer will use the office's default contact information.
                  </AlertDescription>
                </Alert>
              </div>
            </TabsContent>

            {/* SMS Configuration Tab */}
            <TabsContent value="sms" className="space-y-4 mt-4">
              <div className="space-y-4">
                <p className="text-sm text-gray-500">Configure SMS gateway credentials for sending notifications via SMS.</p>

                {/* Current Config Status */}
                {configData.sms_api_url && configData.sms_api_auth && configData.sms_sender_id ? (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <BadgeCheck className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-green-800">SMS Configured</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div>
                        <span className="text-gray-500">API URL</span>
                        <p className="text-gray-800 font-mono truncate">{configData.sms_api_url}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Auth</span>
                        <p className="text-gray-800 font-mono">{'*'.repeat(Math.min(configData.sms_api_auth.length, 20))}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Sender ID</span>
                        <p className="text-gray-800 font-semibold">{configData.sms_sender_id}</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-amber-600" />
                    <span className="text-sm text-amber-800">SMS not configured. Fill in all fields below and save.</span>
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <Label className="text-xs">SMS API URL</Label>
                    <Input
                      data-testid="sms-api-url"
                      placeholder="https://api.smsprovider.com/send"
                      value={configData.sms_api_url}
                      onChange={(e) => setConfigData({ ...configData, sms_api_url: e.target.value })}
                    />
                    <p className="text-xs text-gray-400 mt-1">The endpoint URL for your SMS gateway API</p>
                  </div>
                  <div>
                    <Label className="text-xs">SMS API AUTH</Label>
                    <Input
                      data-testid="sms-api-auth"
                      type="password"
                      placeholder="API key or authentication token"
                      value={configData.sms_api_auth}
                      onChange={(e) => setConfigData({ ...configData, sms_api_auth: e.target.value })}
                    />
                    <p className="text-xs text-gray-400 mt-1">Basic auth token for the SMS gateway</p>
                  </div>
                  <div>
                    <Label className="text-xs">SMS Sender ID</Label>
                    <Input
                      data-testid="sms-sender-id"
                      placeholder="e.g. Ecowater"
                      value={configData.sms_sender_id}
                      onChange={(e) => setConfigData({ ...configData, sms_sender_id: e.target.value })}
                    />
                    <p className="text-xs text-gray-400 mt-1">The sender name that will appear on SMS messages</p>
                  </div>
                </div>

                {/* SMS Test Section */}
                {configData.sms_api_url && configData.sms_api_auth && configData.sms_sender_id && (
                  <div className="p-4 bg-gray-50 border rounded-lg space-y-3">
                    <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      Test SMS
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Recipient Phone</Label>
                        <div className="flex">
                          <select
                            className="inline-flex items-center px-1.5 bg-gray-100 border border-r-0 rounded-l-md text-xs text-gray-600 outline-none cursor-pointer"
                            value={smsTestCountryCode}
                            onChange={(e) => setSmsTestCountryCode(e.target.value)}
                            data-testid="sms-test-country-code"
                          >
                            <option value="+255">+255 (TZ)</option>
                            <option value="+254">+254 (KE)</option>
                            <option value="+256">+256 (UG)</option>
                            <option value="+250">+250 (RW)</option>
                            <option value="+257">+257 (BI)</option>
                            <option value="+243">+243 (CD)</option>
                            <option value="+258">+258 (MZ)</option>
                            <option value="+265">+265 (MW)</option>
                            <option value="+260">+260 (ZM)</option>
                            <option value="+27">+27 (ZA)</option>
                            <option value="+234">+234 (NG)</option>
                            <option value="+233">+233 (GH)</option>
                            <option value="+1">+1 (US)</option>
                            <option value="+44">+44 (UK)</option>
                            <option value="+971">+971 (AE)</option>
                            <option value="+91">+91 (IN)</option>
                          </select>
                          <Input className="rounded-l-none" placeholder="7XXXXXXXX"
                            data-testid="sms-test-phone"
                            value={smsTestPhone}
                            onChange={(e) => setSmsTestPhone(e.target.value)} />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Test Message</Label>
                        <Input placeholder="Test message..."
                          data-testid="sms-test-message"
                          value={smsTestMessage}
                          onChange={(e) => setSmsTestMessage(e.target.value)} />
                      </div>
                    </div>
                    <Button size="sm" data-testid="sms-test-send-btn" onClick={handleTestSms} disabled={smsTesting || !smsTestPhone}
                      className="bg-[#0779BC] hover:bg-[#065d94]">
                      {smsTesting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                      Send Test SMS
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => setShowConfigModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveConfig} disabled={submitting} data-testid="save-config-button">
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Configuration'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageOffices;
